// 조직 탈퇴 — 대표만 · 조직 이름·비밀번호를 다시 쳐야 바로 해지되고, 새로 고친 화면이 해지 안내(복구 기한)를 띄운다.

import { FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";

import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import SectionCard from "@buildpy/ui/section-card";
import { ORG_RETENTION_TEXT } from "@buildpy/core/modules/org/status";
import { leaveOrg } from "@/api/actions/org";
import { settleRedirect } from "@/lib/redirect-error";

export const LEAVE_DONE_PATH = "/app";

const SettingsLeave = ({ orgName }: { orgName: string }) => {
  const [open, setOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const ready = name.trim() === orgName.trim() && password.length > 0;

  const reset = (next: boolean) => {
    if (pending) return;
    setOpen(next);
    if (!next) {
      setName("");
      setPassword("");
      setError("");
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready || pending) return;
    startTransition(async () => {
      try {
        const result = await leaveOrg({ name, password });
        if (!result.ok) {
          setError(result.message);
          return;
        }
        toast.success(result.message);
        window.location.assign(LEAVE_DONE_PATH);
      } catch (caught) {
        if (settleRedirect(caught)) return;
        setError("탈퇴하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return (
    <SectionCard
      data-testid="settings-leave"
      description={`조직을 바로 해지합니다. 멤버 모두 로그인해도 화면을 쓸 수 없고 고객 링크도 닫힙니다. ${ORG_RETENTION_TEXT}.`}
      title="조직 탈퇴">
      <div className="flex flex-col items-start gap-2">
        <Button data-testid="settings-leave-open" size="sm" type="button" variant="destructive" onClick={() => reset(true)}>
          조직 탈퇴
        </Button>
      </div>

      <Dialog onOpenChange={reset} open={open}>
        <DialogContent className="sm:max-w-md" data-testid="settings-leave-dialog">
          <DialogHeader>
            <DialogTitle>조직을 탈퇴할까요?</DialogTitle>
            <DialogDescription>{ORG_RETENTION_TEXT}.</DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-y-4" onSubmit={onSubmit}>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="leave-org-name">조직 이름 「{orgName}」 을 그대로 입력하세요</Label>
              <Input
                autoComplete="off"
                disabled={pending}
                id="leave-org-name"
                name="name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError("");
                }}
              />
            </div>
            <div className="flex flex-col gap-y-1.5">
              <Label htmlFor="leave-password">비밀번호</Label>
              <Input
                autoComplete="current-password"
                disabled={pending}
                id="leave-password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
              />
            </div>
            <p aria-live="polite" className="text-xs text-destructive empty:hidden" data-testid="settings-leave-error">
              {error}
            </p>
            <DialogFooter>
              <Button data-testid="settings-leave-submit" disabled={!ready || pending} size="sm" type="submit" variant="destructive">
                {pending ? "탈퇴하는 중…" : "조직 탈퇴"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};

export default SettingsLeave;
